import { useRef, useEffect } from 'react'

interface Message {
  id: number
  match_id: number
  sender_id: number
  content: string
  content_translated: string | null
  original_locale: string
  culture_hint?: string | null
  is_read: number
  read_at?: string | null
  sent_by: 'user' | 'ai_avatar' | 'offline_responder' | 'persona' | 'system'
  created_at: string
}

interface ChatMessageListProps {
  messages: Message[]
  currentUserId?: number
  showTranslation: Record<number, boolean>
  onTranslate: (messageId: number) => void
  isOtherUserTyping: boolean
  sending: boolean
  sendingMessage: string
  userLocale?: string
  currentStage?: number // 스테이지 1에서는 번역 버튼 숨김
}

export default function ChatMessageList({
  messages,
  currentUserId,
  showTranslation,
  onTranslate,
  isOtherUserTyping,
  sending,
  sendingMessage,
  userLocale = 'ko',
  currentStage = 1,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const localDate = new Date(date.getTime() + (now.getTimezoneOffset() * 60000) + (9 * 60 * 60000)) // UTC to KST
    return localDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })
  }

  const isMyMessage = (senderId: number) => senderId === currentUserId

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 pb-32">
      {messages.map((message) => {
        const isMine = isMyMessage(message.sender_id)
        const showTrans = showTranslation[message.id]

        // System messages (question reveals, mini-game results) - centered with special styling
        // Also detect system messages by content prefix (fallback for messages without sent_by)
        const isSystemMessage = message.sent_by === 'system' ||
          message.content.startsWith('🎁 질문이 공개되었습니다') ||
          message.content.startsWith('🎮 동시답변 게임') ||
          message.content.startsWith('🎉 스테이지')

        if (isSystemMessage) {
          return (
            <div key={message.id} className="flex justify-center my-6">
              <div className="max-w-[85%] bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-2xl p-4 shadow-lg border border-primary/20">
                <div className="text-center space-y-2">
                  <div className="whitespace-pre-wrap text-sm text-base-content/90">
                    {message.content}
                  </div>
                  <div className="text-xs text-base-content/50 mt-2">
                    {formatTime(message.created_at)}
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Regular user messages
        return (
          <div
            key={message.id}
            className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[75%] ${isMine ? 'order-2' : ''}`}>
              {/* 메시지 버블 */}
              <div
                className={`chat ${isMine ? 'chat-end' : 'chat-start'}`}
              >
                <div
                  className={`chat-bubble ${isMine
                    ? 'chat-bubble-primary'
                    : message.sent_by === 'offline_responder'
                      ? 'bg-base-200 text-base-content border border-primary/20'
                      : 'bg-base-100 text-base-content'
                    }`}
                  style={{
                    wordBreak: 'keep-all',
                    overflowWrap: 'break-word',
                    width: 'auto',
                    maxWidth: '100%'
                  }}
                >
                  {/* 원문 */}
                  <p className="whitespace-pre-wrap">{message.content}</p>

                  {/* 번역 (보이기 토글) */}
                  {showTrans && message.content_translated && (
                    <div className="mt-2 pt-2 border-t border-white/20">
                      <p className="text-sm opacity-80">
                        {isMine
                          ? `→ ${message.content_translated}`
                          : message.content_translated}
                      </p>
                      {/* 문화 코칭 힌트 */}
                      {message.culture_hint && (
                        <div className="mt-1 text-xs bg-yellow-100 text-yellow-800 p-1 rounded">
                          💡 {message.culture_hint}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="chat-footer opacity-50 text-xs mt-1 flex items-center gap-1">
                  {formatTime(message.created_at)}
                  {/* 읽음 표시 (내가 보낸 메시지만) */}
                  {isMine && (
                    <span className="ml-1">
                      {message.is_read === 1 ? (
                        // 읽음: 파란색 더블체크
                        <span title="읽음">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-4 h-4 text-blue-500"
                          >
                            <path d="M1.5 12.5l5 5L18 6" stroke="currentColor" strokeWidth="2" fill="none" />
                            <path d="M5.5 12.5l5 5L22 6" stroke="currentColor" strokeWidth="2" fill="none" />
                          </svg>
                        </span>
                      ) : (
                        // 읽지 않음: 회색 체크
                        <span title="전송됨">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            className="w-4 h-4 text-gray-400"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      )}
                    </span>
                  )}
                  {message.sent_by === 'offline_responder' && (
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 ml-1"
                      title="AI 응답"
                    ></span>
                  )}
                </div>
              </div>

              {/* 번역 버튼 (상대방 메시지만, 다른 언어인 경우만, 스테이지 2 이상) */}
              {!isMine && message.original_locale && message.original_locale !== userLocale && currentStage >= 2 && (
                <button
                  onClick={() => onTranslate(message.id)}
                  className="text-xs text-primary ml-12 mt-1 hover:underline"
                >
                  {showTrans ? '원문만 보기' : message.content_translated ? '번역 보기' : '번역하기'}
                </button>
              )}
            </div>
          </div>
        )
      })}

      {/* 타이핑 인디케이터 */}
      {isOtherUserTyping && (
        <div className="flex justify-start">
          <div className="chat chat-start">
            <div className="chat-bubble bg-base-100 text-base-content">
              <span className="loading loading-dots loading-sm"></span>
            </div>
          </div>
        </div>
      )}

      {/* 전송 중 버블 */}
      {sending && sendingMessage && (
        <div className="flex justify-end">
          <div className="max-w-[75%]">
            <div className="chat chat-end">
              <div className="chat-bubble chat-bubble-primary opacity-60 animate-pulse">
                <p>{sendingMessage}</p>
                <div className="flex gap-1 items-center justify-end mt-1">
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
              <div className="chat-footer opacity-50 text-xs mt-1">
                전송 중...
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  )
}
